import {Button, createStyles, Text, Tooltip, useMantineTheme} from '@mantine/core';
import React, {ReactNode, useMemo, useRef} from 'react';
import {ConnectionLineType, MarkerType, ReactFlow, ReactFlowProvider} from 'reactflow';

import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import staticURL from 'lib/staticURL';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import ReactFlowWrapper from 'processes/graph-builder/flowchart-viz/ReactFlowWrapper';
import {ExperimentMethod} from 'studies/typed/shared/procedure-runs/design-state/ExperimentalInputs';
import {ConditionGroup} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';
import getTestProps from 'test/lib/cypress/getTestProps';
import useResizeObserver from 'ui-library/hooks/useResizeObserver';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import DefaultBackground from 'ui-library/reactflow/backgrounds/DefaultBackground';
import HorizontalControls, {MIN_ZOOM, MAX_ZOOM} from 'ui-library/reactflow/controls/HorizontalControls';
import {REACT_FLOW_PRO_OPTIONS} from 'ui-library/reactflow/ReactFlowConstants';
import {FLOWCHART_SIDEPANEL_Z_INDEX} from 'ui-library/reactflow/sidepanels/FlowchartSidebar';

import {useConditionReplicateRemoval} from '../../procedure-runs/conditions/useConditionReplicateRemoval';
import {ProcedureRunDesignFlowchartAddReplicateContext} from './procedureRunDesignFlowchartAddReplicateContext';
import ProcedureRunDesignFlowchartAddReplicateNode, {
  PROCEDURE_RUN_DESIGN_FLOWCHART_ADD_REPLICATE_NODE_TYPE,
} from './ProcedureRunDesignFlowchartAddReplicateNode';
import ProcedureRunDesignFlowchartColumnNode, {
  isProcedureRunDesignFlowchartColumnNode,
  PROCEDURE_RUN_DESIGN_FLOWCHART_COLUMN_NODE_TYPE,
} from './ProcedureRunDesignFlowchartColumnNode';
import ProcedureRunDesignFlowchartConfirmRemoveModal from './ProcedureRunDesignFlowchartConfirmRemoveModal';
import type {FlowchartConnectionOptions} from './procedureRunDesignFlowchartConnectionUtils';
import {EDGE_TYPES, getProcedureRunDesignFlowchartEdgeColor} from './ProcedureRunDesignFlowchartEdge';
import {ProcedureRunDesignFlowchartEdgeActionsContext} from './procedureRunDesignFlowchartEdgeActionsContext';
import {ProcedureRunDesignFlowchartEditableContext} from './procedureRunDesignFlowchartEditableContext';
import ProcedureRunDesignFlowchartGhostNode, {
  PROCEDURE_RUN_DESIGN_FLOWCHART_GHOST_NODE_TYPE,
} from './ProcedureRunDesignFlowchartGhostNode';
import {FlowchartEdgeSpec, StatusByNodeId} from './ProcedureRunDesignFlowchartLayoutUtils';
import ProcedureRunDesignFlowchartNode, {
  isProcedureRunDesignFlowchartNode,
  PROCEDURE_RUN_DESIGN_FLOWCHART_NODE_TYPE,
} from './ProcedureRunDesignFlowchartNode';
import {ProcedureRunDesignFlowchartNodeActionsContext} from './procedureRunDesignFlowchartNodeActionsContext';
import {ProcedureRunDesignFlowchartShowArchivedContext} from './procedureRunDesignFlowchartShowArchivedContext';
import {useProcedureRunDesignFlowchartAutoScroll} from './useProcedureRunDesignFlowchartAutoScroll';
import type {MethodColumnScrollMode} from './useProcedureRunDesignFlowchartAutoScroll';
import {useProcedureRunDesignFlowchartConnectionHandlers} from './useProcedureRunDesignFlowchartConnectionHandlers';
import {useProcedureRunDesignFlowchartHover} from './useProcedureRunDesignFlowchartHover';
import {useProcedureRunDesignFlowchartLayout} from './useProcedureRunDesignFlowchartLayout';
import type {ProcedureRunDesignFlowchartLayoutOptions} from './useProcedureRunDesignFlowchartLayout';

const LAYOUT_OPTIONS: ProcedureRunDesignFlowchartLayoutOptions = {
  horizontalGap: 75,
  verticalGap: 32,
  startX: 24,
  // Leave enough top headroom for the active method label rendered above the column border.
  startY: 40,
  columnHeaderBottomGap: 20,
};

const TOP_BANNER_TOP_OFFSET = 16;

const NODE_TYPES = {
  [PROCEDURE_RUN_DESIGN_FLOWCHART_NODE_TYPE]: ProcedureRunDesignFlowchartNode,
  [PROCEDURE_RUN_DESIGN_FLOWCHART_COLUMN_NODE_TYPE]: ProcedureRunDesignFlowchartColumnNode,
  [PROCEDURE_RUN_DESIGN_FLOWCHART_ADD_REPLICATE_NODE_TYPE]: ProcedureRunDesignFlowchartAddReplicateNode,
  [PROCEDURE_RUN_DESIGN_FLOWCHART_GHOST_NODE_TYPE]: ProcedureRunDesignFlowchartGhostNode,
};

const STATIC_PROPS: Partial<React.ComponentProps<typeof ReactFlow>> = {
  nodesDraggable: false,
  // Disable ReactFlow keyboard delete so removals always route through onRemoveEdge.
  deleteKeyCode: null,
  panOnScroll: true,
  panOnDrag: true,
  zoomOnScroll: false,
  zoomOnPinch: true,
  defaultViewport: {x: 0, y: 0, zoom: 1},
  minZoom: MIN_ZOOM,
  maxZoom: MAX_ZOOM,
  proOptions: REACT_FLOW_PRO_OPTIONS,
  connectionLineType: ConnectionLineType.Bezier,
  connectionLineStyle: {stroke: getProcedureRunDesignFlowchartEdgeColor('editable'), strokeWidth: 1.5},
};

const EMPTY_NODE_SET: ReadonlySet<string> = new Set();
const EMPTY_EXPERIMENTAL_INPUTS: ExperimentalInputs = {};

/**
 * Optional native HTML5 drag-and-drop wiring for the flowchart canvas. Used by the procedure-less
 * empty state to accept a procedure card dragged from the search sidepanel and dropped onto the
 * empty canvas.
 */
export interface ProcedureRunDesignFlowchartCanvasDnd {
  /** When true, the canvas renders an active drop-target highlight. */
  isDropTargetActive: boolean;
  onDragEnter: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: (event: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
}

interface ProcedureRunDesignFlowchartProps {
  conditions: Array<ConditionGroup>;
  methods: Array<ExperimentMethod>;
  activeMethodId: string | null;
  procedureType: ProcedureType;
  connectionOptions: FlowchartConnectionOptions;
  experimentalInputs?: ExperimentalInputs;
  /** The single edge list to render (derived + drawn, already merged upstream). */
  edgeSpecs: Array<FlowchartEdgeSpec>;
  /** Records a newly drawn edge in the draft edge state. */
  onDrawEdge: (spec: FlowchartEdgeSpec) => void;
  /** Removes an edge by id and records the deletion in the edit state. */
  onRemoveEdge: (edgeId: string) => void;
  /** Removes a draft condition replicate from parent-owned condition state. */
  onRemoveDraftConditionReplicate?: (conditionIndex: number, replicateIndex: number) => void;
  /** Node ids removed in parent-owned draft state. */
  removedNodeIds?: ReadonlySet<string>;
  /** Records removed persisted flowchart node ids in parent-owned draft state. */
  onRemovePersistedConditionReplicate?: (nodeIds: ReadonlySet<string>) => void;
  topBanner?: ReactNode;
  controlsShiftLeft?: number;
  canvasDnd?: ProcedureRunDesignFlowchartCanvasDnd;
  /**
   * The currently selected method ID. Drives the column highlight and stays in sync with external
   * state. When null no column is highlighted as selected.
   */
  selectedMethodId: string | null;
  /**
   * Sets the selected method ID. Called with the clicked column's method ID when a column or grid
   * node is clicked, and with null when the pane (background) is clicked to clear the selection.
   */
  setSelectedMethodId: (methodId: string | null) => void;
  /**
   * One-shot pan request from the method dropdown. Cleared via {@link onMethodScrollConsumed} once
   * handled.
   */
  pendingMethodIdToScroll?: string | null;
  /**
   * {@link MethodColumnScrollMode} for {@link pendingMethodIdToScroll}. Defaults to {@code always}.
   */
  pendingMethodScrollMode?: MethodColumnScrollMode;
  /** Clears {@link pendingMethodIdToScroll} after the hook handles the request. */
  onMethodScrollConsumed?: () => void;
  /**
   * The absolutely positioned right ("Design") sidepanel. Passed through to auto-scroll so the
   * dropdown-selected method column centers in the canvas still visible beside the panel.
   */
  rightSidepanelRef?: React.RefObject<HTMLDivElement>;
  /**
   * When false, persisted conditions render a lock icon
   */
  allowEditingPersistedConditions: boolean;

  statusByNodeId?: StatusByNodeId;
  /** When true, include archived cells/rows (rendered standalone, with no edges/status); otherwise hide them. */
  showArchived?: boolean;
  /** Whether the flowchart is being edited. */
  isEditable: boolean;
  /**
   * Adds a new condition-replicate lane when the add-replicate button node is clicked. Defaults to a
   * no-op (the button only renders in the split-pool run editor, which supplies this).
   */
  onAddConditionReplicate?: () => void;
  /** Disables the add-replicate button (e.g. at the replicate limit); clicks become no-ops. */
  isAddConditionReplicateDisabled?: boolean;
  /** Tooltip shown on the disabled add-replicate button explaining the limit. */
  addConditionReplicateDisabledTooltip?: string;
  /** Adds edges to ensure all eligible nodes are connected. */
  onAutomapEdges?: () => void;
  /** Disables the automapping control. */
  isAutomapEdgesDisabled?: boolean;
}

interface ProcedureRunDesignFlowchartContentProps extends ProcedureRunDesignFlowchartProps {
  pendingRemovalNodeIds: ReadonlySet<string>;
  removedNodeIds: ReadonlySet<string>;
  lastAddSourceRef: React.MutableRefObject<'inCanvas' | null>;
}

function ProcedureRunDesignFlowchartContent({
  conditions,
  methods,
  activeMethodId,
  procedureType,
  connectionOptions,
  experimentalInputs = EMPTY_EXPERIMENTAL_INPUTS,
  controlsShiftLeft,
  selectedMethodId,
  setSelectedMethodId,
  allowEditingPersistedConditions,
  statusByNodeId,
  edgeSpecs,
  onDrawEdge,
  isEditable,
  showArchived = false,
  canvasDnd,
  topBanner,
  pendingRemovalNodeIds,
  removedNodeIds,
  lastAddSourceRef,
  pendingMethodIdToScroll,
  pendingMethodScrollMode,
  onMethodScrollConsumed,
  rightSidepanelRef,
  onAutomapEdges,
  isAutomapEdgesDisabled,
}: ProcedureRunDesignFlowchartContentProps): JSX.Element {
  const {classes, cx} = useStyles();
  const theme = useMantineTheme();
  const {ref: topBannerRef, height: topBannerHeight = 0} = useResizeObserver<HTMLDivElement>();
  const hasTopBanner = topBanner != null;
  const layoutOptions = useMemo(
    () =>
      hasTopBanner
        ? {
            ...LAYOUT_OPTIONS,
            startY: LAYOUT_OPTIONS.startY + TOP_BANNER_TOP_OFFSET + topBannerHeight,
          }
        : LAYOUT_OPTIONS,
    [hasTopBanner, topBannerHeight]
  );
  const {hoveredMethodId, hoveredNodeId, onNodeMouseEnter, onNodeMouseLeave} =
    useProcedureRunDesignFlowchartHover();
  const {nodes, edges, liveEdges, onNodesChange, onEdgesChange} = useProcedureRunDesignFlowchartLayout({
    conditions,
    methods,
    activeMethodId,
    procedureType,
    experimentalInputs,
    layoutOptions,
    methodHighlightState: {
      hoveredMethodId,
      hoveredNodeId,
      selectedMethodId,
    },
    allowEditingPersistedConditions,
    statusByNodeId,
    edgeSpecs,
    showArchived,
    isEditable,
    pendingRemovalNodeIds,
    removedNodeIds,
  });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const {additionalAutoScrollControls} = useProcedureRunDesignFlowchartAutoScroll({
    conditions,
    activeMethodId,
    isEditable,
    nodes,
    reactFlowWrapper: wrapperRef,
    lastAddSourceRef,
    pendingMethodIdToScroll,
    pendingMethodScrollMode,
    onMethodScrollConsumed,
    selectedMethodId,
    rightSidepanelRef,
  });
  const showEmptyState = methods.length === 0;

  const {nodesConnectable, isValidConnection, onConnect} = useProcedureRunDesignFlowchartConnectionHandlers({
    nodes,
    edges: liveEdges,
    onDrawEdge,
    activeMethodId,
    isEditable,
    connectionOptions,
  });

  return (
    <div
      ref={wrapperRef}
      className={cx(classes.flowchartContainer, canvasDnd?.isDropTargetActive && classes.activeDropTarget)}
      {...getTestProps({component: 'ProcedureRunDesignFlowchart', element: 'root'})}
      onDragEnter={canvasDnd?.onDragEnter}
      onDragOver={canvasDnd?.onDragOver}
      onDragLeave={canvasDnd?.onDragLeave}
      onDrop={canvasDnd?.onDrop}
    >
      {topBanner != null && (
        <div ref={topBannerRef} className={classes.topBannerContainer}>
          {topBanner}
        </div>
      )}
      <ReactFlow
        nodes={nodes}
        onNodesChange={onNodesChange}
        edges={edges}
        onEdgesChange={onEdgesChange}
        edgeTypes={EDGE_TYPES}
        defaultEdgeOptions={{markerEnd: {type: MarkerType.Arrow, color: theme.colors.gray[6]}}}
        nodeTypes={NODE_TYPES}
        {...STATIC_PROPS}
        nodesConnectable={nodesConnectable}
        isValidConnection={isValidConnection}
        onConnect={onConnect}
        onNodeMouseEnter={onNodeMouseEnter}
        onNodeMouseLeave={onNodeMouseLeave}
        onPaneClick={() => {
          setSelectedMethodId(null);
        }}
        onNodeClick={(_e, node) => {
          if (isProcedureRunDesignFlowchartNode(node) || isProcedureRunDesignFlowchartColumnNode(node)) {
            setSelectedMethodId(node.data.methodId);
          }
        }}
      >
        {!showEmptyState && (
          <HorizontalControls
            shiftLeft={controlsShiftLeft ?? 0}
            additionalControls={additionalAutoScrollControls}
            secondaryControls={
              onAutomapEdges != null ? (
                <Tooltip label="All conditions are mapped." disabled={!isEditable || !isAutomapEdgesDisabled}>
                  {/* Disabled buttons do not receive pointer events, so the wrapper triggers the tooltip. */}
                  <span
                    className={classes.automapConditionsTooltipTarget}
                    {...getTestProps({
                      component: 'ProcedureRunDesignFlowchart',
                      element: 'automapConditionsTooltipTarget',
                    })}
                  >
                    <Button
                      className={classes.automapButton}
                      compact={true}
                      disabled={!isEditable || isAutomapEdgesDisabled}
                      leftIcon={<BenchlingIcon icon="split" />}
                      onClick={onAutomapEdges}
                      variant="ghost"
                      {...getTestProps({
                        component: 'ProcedureRunDesignFlowchart',
                        element: 'automapConditionsButton',
                      })}
                    >
                      Auto-map conditions
                    </Button>
                  </span>
                </Tooltip>
              ) : undefined
            }
          />
        )}
        <DefaultBackground />
      </ReactFlow>
      {showEmptyState && <ProcedureRunDesignFlowchartEmptyState procedureType={procedureType} />}
    </div>
  );
}

export default function ProcedureRunDesignFlowchart(props: ProcedureRunDesignFlowchartProps): JSX.Element {
  const lastAddSourceRef = useRef<'inCanvas' | null>(null);
  const edgeActions = useMemo(() => ({onRemoveEdge: props.onRemoveEdge}), [props.onRemoveEdge]);
  const {onAddConditionReplicate, isAddConditionReplicateDisabled, addConditionReplicateDisabledTooltip} =
    props;
  const addReplicate = useMemo(
    () => ({
      onAdd: () => {
        lastAddSourceRef.current = 'inCanvas';
        (onAddConditionReplicate ?? (() => {}))();
      },
      isDisabled: isAddConditionReplicateDisabled ?? false,
      disabledTooltipText: addConditionReplicateDisabledTooltip,
    }),
    [onAddConditionReplicate, isAddConditionReplicateDisabled, addConditionReplicateDisabledTooltip]
  );
  const {pendingRemovalNodeIds, confirmation, nodeActionsContextValue} = useConditionReplicateRemoval({
    conditions: props.conditions,
    methods: props.methods,
    isEditable: props.isEditable,
    onRemoveDraftConditionReplicate: props.onRemoveDraftConditionReplicate,
    onRemovePersistedConditionReplicate: props.onRemovePersistedConditionReplicate,
  });
  return (
    <ProcedureRunDesignFlowchartEditableContext.Provider value={props.isEditable}>
      <ProcedureRunDesignFlowchartShowArchivedContext.Provider value={props.showArchived ?? false}>
        <ProcedureRunDesignFlowchartAddReplicateContext.Provider value={addReplicate}>
          <ProcedureRunDesignFlowchartEdgeActionsContext.Provider value={edgeActions}>
            <ProcedureRunDesignFlowchartNodeActionsContext.Provider value={nodeActionsContextValue}>
              <ReactFlowProvider>
                <ReactFlowWrapper fullWidth={true}>
                  <ProcedureRunDesignFlowchartContent
                    {...props}
                    pendingRemovalNodeIds={pendingRemovalNodeIds}
                    removedNodeIds={props.removedNodeIds ?? EMPTY_NODE_SET}
                    lastAddSourceRef={lastAddSourceRef}
                  />
                </ReactFlowWrapper>
              </ReactFlowProvider>
              {confirmation != null && (
                <ProcedureRunDesignFlowchartConfirmRemoveModal confirmation={confirmation} />
              )}
            </ProcedureRunDesignFlowchartNodeActionsContext.Provider>
          </ProcedureRunDesignFlowchartEdgeActionsContext.Provider>
        </ProcedureRunDesignFlowchartAddReplicateContext.Provider>
      </ProcedureRunDesignFlowchartShowArchivedContext.Provider>
    </ProcedureRunDesignFlowchartEditableContext.Provider>
  );
}

function ProcedureRunDesignFlowchartEmptyState({procedureType}: {procedureType: ProcedureType}): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles();
  const procedureWithArticle = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
    stringCase: StringCase.LOWER,
    prependAorAn: true,
  });

  return (
    <div
      className={classes.emptyStateOverlay}
      {...getTestProps({component: 'ProcedureRunDesignFlowchart', element: 'emptyState'})}
    >
      <img
        src={staticURL('/static/img/studies/unit-op-instance-linking/EmptyFlowchart.svg')}
        alt="Empty flowchart illustration"
      />
      <Text className={classes.emptyStateText}>{`Drag ${procedureWithArticle} to start planning`}</Text>
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  flowchartContainer: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  activeDropTarget: {
    boxShadow: `inset 0 0 0 2px ${theme.colors.blue[5]}`,
  },
  automapButton: {
    // Override the Benchling button style to align more with the ReactFlow ControlButton style, since it's
    // rendered right next to the control buttons
    '&&': {
      color: theme.black,
      fontSize: theme.fontSizes.sm,
    },
    '&&:disabled, &&[data-disabled]': {
      color: theme.colors.gray[5],
    },
  },
  automapConditionsTooltipTarget: {
    display: 'inline-flex',
  },
  emptyStateOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyStateText: {
    marginTop: theme.spacing.md,
    color: theme.colors.gray[7],
    fontSize: theme.fontSizes.md,
    fontWeight: 600,
    textAlign: 'center',
  },
  topBannerContainer: {
    position: 'absolute',
    top: TOP_BANNER_TOP_OFFSET,
    left: theme.spacing.md,
    right: theme.spacing.md,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.sm,
    zIndex: FLOWCHART_SIDEPANEL_Z_INDEX - 1,
  },
}));
